import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAppStore } from "@/store";

export default function Terms() {
  const hasAccount = useAppStore((s) => s.hasAccount);
  const backPath = hasAccount ? "/settings" : "/login";
  const backLabel = hasAccount ? "Back to Settings" : "Back to Login";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto py-8 px-4"
    >
      <Link href={backPath}>
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer mb-6 block">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </span>
      </Link>

      <h1 className="text-3xl font-display font-bold mb-2">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-8">Last updated: March 13, 2026</p>

      <div className="prose prose-invert prose-sm max-w-none space-y-6 text-foreground/90">
        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">1. Acceptance of Terms</h2>
          <p>By accessing or using ScribPost ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">2. Description of Service</h2>
          <p>ScribPost is an AI-powered content creation platform that helps users generate social media posts, threads, and replies in their unique writing voice. The Service includes voice training, post generation, idea management, creator tools, and smart reply features.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">3. User Accounts</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>You must provide a valid email address and create a password to register.</li>
            <li>You are responsible for maintaining the security of your account credentials.</li>
            <li>You must be at least 16 years old to use the Service.</li>
            <li>One account per person. Automated account creation is prohibited.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">4. Acceptable Use</h2>
          <p>You agree not to use ScribPost to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Generate spam, misleading, or fraudulent content.</li>
            <li>Impersonate other individuals or organizations.</li>
            <li>Generate content that is illegal, harmful, threatening, abusive, or hateful.</li>
            <li>Violate the terms of service of any social media platform where content is posted.</li>
            <li>Attempt to reverse-engineer, exploit, or disrupt the Service.</li>
            <li>Use automated systems to access the Service in a manner that exceeds reasonable use.</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">5. Subscriptions & Billing</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Free tier:</strong> Limited to 3 AI-generated posts. No credit card required.</li>
            <li><strong>Pro plan:</strong> Available as monthly or yearly subscription. Unlocks unlimited post generation and all creator tools.</li>
            <li>Payments are processed securely through Stripe. We do not store your payment card details.</li>
            <li>Subscriptions automatically renew unless canceled before the end of the billing period.</li>
            <li>You may cancel your subscription at any time through the billing management portal. Access continues until the end of the current paid period.</li>
            <li>Refunds are handled on a case-by-case basis. Contact support for assistance.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">6. Content Ownership</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Your content:</strong> You retain full ownership of all content you provide (writing samples, ideas, brainstorms) and all content generated for you by the Service.</li>
            <li><strong>License to us:</strong> By using the Service, you grant us a limited license to process your content solely to provide the Service (e.g., sending text to AI models for generation).</li>
            <li><strong>No claim:</strong> We make no ownership claim over your generated posts, ideas, or any other user content.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">7. AI-Generated Content</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Content generated by ScribPost is produced using AI language models and may not always be accurate, appropriate, or suitable for your intended purpose.</li>
            <li>You are solely responsible for reviewing, editing, and publishing any AI-generated content.</li>
            <li>We do not guarantee that generated content is free from errors, bias, or infringement.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">8. Privacy</h2>
          <p>Your use of the Service is also governed by our <Link href="/privacy"><span className="text-primary hover:underline cursor-pointer">Privacy Policy</span></Link>, which describes how we collect, use, and protect your data.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">9. Limitation of Liability</h2>
          <p>To the maximum extent permitted by applicable law:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>The Service is provided "as is" without warranties of any kind, express or implied.</li>
            <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</li>
            <li>Our total liability for any claim related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</li>
            <li>We are not responsible for content you post on third-party platforms using the Service.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">10. Service Availability</h2>
          <p>We strive to keep ScribPost available at all times but do not guarantee uninterrupted access. We may perform maintenance, updates, or modifications that temporarily affect availability.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">11. Termination</h2>
          <p>You may delete your account at any time from Settings. We may suspend or terminate your account for violations of these terms. Upon termination, all your data will be permanently deleted.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">12. Changes to Terms</h2>
          <p>We may update these terms from time to time. Material changes will be communicated via email or in-app notice. Continued use after changes constitutes acceptance of the updated terms.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">13. Contact</h2>
          <p>For questions about these terms, contact us at <a href="mailto:support@scribpost.com" className="text-primary hover:underline">support@scribpost.com</a>.</p>
        </section>
      </div>
    </motion.div>
  );
}
